import { formatDateTime, isoAttr } from "../../lib/datetime.ts";
import { type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import {
  DenseTable,
  StatusBadge,
  defaultNumberFormat,
  type Column,
} from "../../components/index.ts";
import type { EnrolmentFunnel as EnrolmentFunnelData } from "./use-identity.ts";
import {
  FUNNEL_ORDER,
  stageLabel,
  formatConversion,
  formatDurationQuantity,
  formatDurationSeconds,
  quantityMeasure,
  deriveStuckPending,
  funnelStageHref,
  terminalFailureHref,
  stuckPendingHref,
} from "./vocab.ts";

/**
 * The enrolment funnel region (task 11.7; design 12.2 Identity "enrolment funnel (start → poll →
 * activated → bound) with conversion and completed time-to-complete; stuck-pending …"; 12.3 drill-
 * down; 12.4 semantic structure, not colour-only).
 *
 * It renders four things from the one `EnrolmentFunnel` snapshot:
 *
 * - The ordered funnel start → poll → activated → bound, each stage a semantic row with its count,
 *   step-over-step conversion and conversion-from-start. Each count drills to the enrolment events
 *   at that stage. The structure is a real table (not colour-coded bars alone), so it is legible to
 *   assistive tech (design 12.4).
 * - The COMPLETED time-to-complete statistics (p50/p95), labelled as covering completed enrolments
 *   only — and, per the named behaviour, EXCLUDING stuck-pending enrolments.
 * - Terminal failures (denied / rejected), each drilling to its events.
 * - The stuck-pending panel, which makes the three named behaviours (FR-4.1) visible and distinct:
 *     1. NON-CONVERTED — the panel states the stuck count as a subset of the funnel's non-converted
 *        total, so it is unmistakable they count against conversion, not toward it.
 *     2. EXCLUDED FROM COMPLETED TIME-TO-COMPLETE — the panel sits apart from the completed stats and
 *        says in words that those duration stats exclude these entries (the data comes from the
 *        `stuck_pending` field, never from `completed`, so they are never mixed).
 *     3. CURRENT AGE — the oldest age and every entry's current age are shown.
 */

export interface EnrolmentFunnelProps {
  readonly data: EnrolmentFunnelData;
}

type StageRow = EnrolmentFunnelData["stages"][number];
type TerminalRow = EnrolmentFunnelData["terminal_failures"][number];
type StuckItem = EnrolmentFunnelData["stuck_pending"]["items"][number];

export function EnrolmentFunnel(props: EnrolmentFunnelProps): ReactNode {
  const navigate = useNavigate();
  const { data } = props;

  // Order the stages by the canonical funnel order, keeping only the four funnel stages here (the
  // terminal failures denied/rejected are shown separately below).
  const stageByName = new Map(data.stages.map((s) => [s.stage, s] as const));
  const orderedStages = FUNNEL_ORDER.map((name) => stageByName.get(name)).filter(
    (s): s is StageRow => s !== undefined,
  );

  const stuck = deriveStuckPending(data);

  const stageColumns: readonly Column<StageRow>[] = [
    {
      key: "stage",
      header: "Stage",
      isRowHeader: true,
      cell: (r) => (
        <button
          type="button"
          className="idn-link"
          onClick={() => navigate(funnelStageHref(r.stage))}
          aria-label={`Open the enrollment events at the ${stageLabel(r.stage).toLowerCase()} stage`}
        >
          {stageLabel(r.stage)}
        </button>
      ),
    },
    {
      key: "count",
      header: "Reached",
      align: "right",
      cell: (r) => <span className="idn-num">{defaultNumberFormat(r.count)}</span>,
    },
    {
      key: "from_prev",
      header: "Conv. from previous",
      align: "right",
      cell: (r) => <span className="idn-num">{formatConversion(r.conversion_from_previous)}</span>,
    },
    {
      key: "from_start",
      header: "Conv. from start",
      align: "right",
      cell: (r) => <span className="idn-num">{formatConversion(r.conversion_from_start)}</span>,
    },
  ];

  return (
    <section className="idn-funnel" aria-label="Enrollment funnel">
      <div className="idn-section__head">
        <h2 className="idn-section__title">Enrollment funnel</h2>
        <span className="idn-section__note">start → poll → activated → bound</span>
      </div>

      <DenseTable
        caption="Enrollment funnel stages with conversion"
        columns={stageColumns}
        rows={orderedStages}
        rowKey={(r) => r.stage}
        emptyMessage="No enrolments were recorded in this window."
      />

      <CompletedStats completed={data.completed} />

      <TerminalFailures rows={data.terminal_failures} onDrill={(stage) => navigate(terminalFailureHref(stage))} />

      <StuckPendingPanel stuck={stuck} onDrill={() => navigate(stuckPendingHref())} />
    </section>
  );
}

/**
 * The completed time-to-complete statistics (p50/p95). Labelled explicitly as covering ONLY
 * completed enrolments and excluding stuck-pending — the second named behaviour, stated where a
 * reader meets the duration numbers so there is no ambiguity.
 */
function CompletedStats(props: { completed: EnrolmentFunnelData["completed"] }): ReactNode {
  const { completed } = props;
  const p50Measure = quantityMeasure(completed.time_to_complete_ms.p50);
  const p95Measure = quantityMeasure(completed.time_to_complete_ms.p95);

  return (
    <div className="idn-completed">
      <h3 className="idn-subhead">Completed time-to-complete</h3>
      <p className="idn-completed__note">
        Over the {defaultNumberFormat(completed.count)} enrollment(s) that reached <code>bound</code>.
        Stuck-pending enrolments are excluded from these figures.
      </p>
      <dl className="idn-completed__grid">
        <div className="idn-stat">
          <dt className="idn-stat__label">Median (p50)</dt>
          <dd className="idn-stat__value" data-measure={p50Measure}>
            {formatDurationQuantity(completed.time_to_complete_ms.p50)}
          </dd>
        </div>
        <div className="idn-stat">
          <dt className="idn-stat__label">p95</dt>
          <dd className="idn-stat__value" data-measure={p95Measure}>
            {formatDurationQuantity(completed.time_to_complete_ms.p95)}
          </dd>
        </div>
      </dl>
    </div>
  );
}

/** Terminal enrolment failures (denied / rejected), each drilling to its events. */
function TerminalFailures(props: {
  rows: readonly TerminalRow[];
  onDrill: (stage: TerminalRow["stage"]) => void;
}): ReactNode {
  const columns: readonly Column<TerminalRow>[] = [
    {
      key: "stage",
      header: "Outcome",
      isRowHeader: true,
      cell: (r) => (
        <button
          type="button"
          className="idn-link"
          onClick={() => props.onDrill(r.stage)}
          aria-label={`Open the ${stageLabel(r.stage).toLowerCase()} enrollment events`}
        >
          {stageLabel(r.stage)}
        </button>
      ),
    },
    {
      key: "count",
      header: "Count",
      align: "right",
      cell: (r) => <span className="idn-num">{defaultNumberFormat(r.count)}</span>,
    },
  ];
  return (
    <div className="idn-terminal">
      <h3 className="idn-subhead">Terminal failures</h3>
      <DenseTable
        caption="Terminal enrollment failures"
        captionHidden
        columns={columns}
        rows={props.rows}
        rowKey={(r) => r.stage}
        emptyMessage="No terminal enrollment failures in this window."
      />
    </div>
  );
}

/**
 * The stuck-pending panel. It makes the three named behaviours (FR-4.1) explicit and distinct:
 *
 * 1. NON-CONVERTED — the headline states the stuck count AS a subset of the funnel's non-converted
 *    total ("N of M non-converted"), so it reads as counting against conversion, never toward it.
 * 2. EXCLUDED FROM COMPLETED — a plain-language note says these entries are left out of the
 *    completed time-to-complete stats; the ages shown here come from `stuck_pending`, never from
 *    `completed`, so the two cannot merge.
 * 3. CURRENT AGE — the oldest age is in the headline and every entry lists its current age.
 */
function StuckPendingPanel(props: {
  stuck: ReturnType<typeof deriveStuckPending>;
  onDrill: () => void;
}): ReactNode {
  const { stuck } = props;
  const hasStuck = stuck.stuckCount > 0;
  const level = hasStuck ? "warn" : "neutral";

  const columns: readonly Column<StuckItem>[] = [
    {
      key: "entry",
      header: "Entry",
      isRowHeader: true,
      width: "lg",
      cell: (r) => <code className="idn-mono">{r.entry_id}</code>,
    },
    {
      key: "stage",
      header: "Stuck at",
      cell: (r) => stageLabel(r.stage),
    },
    {
      key: "started",
      header: "Started",
      cell: (r) => (
        <time dateTime={isoAttr(r.started_at)}>
          {formatDateTime(r.started_at)}
        </time>
      ),
    },
    {
      key: "age",
      header: "Current age",
      align: "right",
      cell: (r) => <span className="idn-num">{formatDurationSeconds(r.age_s)}</span>,
    },
  ];

  return (
    <div className="idn-stuck" data-active={hasStuck}>
      <div className="idn-section__head">
        <h3 className="idn-subhead">Stuck-pending enrolments</h3>
        <StatusBadge
          level={level}
          label={hasStuck ? "Pending past threshold" : "None stuck"}
          size="sm"
          title={
            hasStuck
              ? `${stuck.stuckCount} enrollment(s) have been pending longer than ${formatDurationSeconds(stuck.thresholdS)}.`
              : "No enrolments are pending past the stuck threshold."
          }
        />
      </div>

      {/* Behaviour 1 (non-converted) + behaviour 3 (oldest age), stated plainly. */}
      <p className="idn-stuck__headline">
        <button
          type="button"
          className="idn-link idn-link--strong"
          onClick={props.onDrill}
          aria-label="Open the stuck-pending enrollment events"
        >
          {defaultNumberFormat(stuck.stuckCount)}
        </button>{" "}
        of {defaultNumberFormat(stuck.nonConverted)} non-converted enrollment(s) are stuck past{" "}
        {formatDurationSeconds(stuck.thresholdS)}
        {stuck.oldestAgeS !== null && (
          <> · oldest {formatDurationSeconds(stuck.oldestAgeS)}</>
        )}
        .
      </p>

      {/* Behaviour 2 (excluded from completed time-to-complete), stated plainly. */}
      <p className="idn-stuck__note">
        These remain non-converted funnel entries and are excluded from the completed
        time-to-complete statistics above; their current ages are shown below.
      </p>

      {hasStuck && (
        <DenseTable
          caption="Stuck-pending enrolments with current age"
          columns={columns}
          rows={stuck.items}
          rowKey={(r) => r.entry_id}
          stickyHeader
          emptyMessage="No individual stuck entries were returned."
        />
      )}
    </div>
  );
}
